import { useId, useRef, useState, useCallback, useEffect, type CSSProperties } from 'react';
import { useAnimationFrame } from '../viz/useAnimationFrame';
import {
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../viz/vizShared';

// Titration burette: fine 0.1 mL graduations (numbered 0 at TOP, increasing
// DOWNWARD — the way a real burette reads), a rotary stopcock, dropwise titrant
// delivery, a live volume / dispensed readout, and "add a drop" / "hold to flow"
// controls. Physical glass colors kept; stopcock + readout accents use --ws-*.
//
// CUE CONTRACT (all optional, additive): cue.progress (0..1) sets how much has
// been dispensed from the start volume; cue.play=true holds the stopcock open and
// streams drops; highlight token 'drop' delivers a single drop. With no cue the
// component is fully interactive on its own.

export interface BuretteProps {
  volume?: number; // current volume in mL
  maxVolume?: number; // capacity (typically 50 mL)
  liquidColor?: string;
  stopcock?: 'open' | 'closed';
  onStopcock?: () => void;
  /** Controlled-style hooks (optional). Fired as titrant is delivered. */
  onDispense?: (info: { volume: number; dispensed: number }) => void;
  disabled?: boolean;
  className?: string;
  /** Narration-sync cue forwarded by the lecture player. */
  cue?: VizCue;
}

const DROP_ML = 0.05; // ~ one drop

export default function Burette({
  volume = 25,
  maxVolume = 50,
  liquidColor = '#7DD3FC',
  stopcock = 'closed',
  onStopcock,
  onDispense,
  disabled = false,
  className = '',
  cue,
}: BuretteProps) {
  const id = useId();
  const reduced = usePrefersReducedMotion();
  const cueOn = cueIsActive(cue);
  const tokens = cueHighlights(cue);

  // The component owns its own volume so it can self-demonstrate and run the
  // drop animation; it seeds from the incoming `volume` prop and follows it when
  // the prop changes (uncontrolled-with-sync).
  const startRef = useRef(volume);
  const [internalVol, setInternalVol] = useState(volume);
  const lastPropVol = useRef(volume);
  useEffect(() => {
    if (lastPropVol.current !== volume) {
      lastPropVol.current = volume;
      startRef.current = volume;
      setInternalVol(volume);
    }
  }, [volume]);

  // Cue.progress drives dispensed volume from the original start.
  const cueVol = cue?.progress != null
    ? Math.max(0, startRef.current - cue.progress * startRef.current)
    : null;

  const [flowing, setFlowing] = useState(false);
  const cueFlow = cueOn && cue?.play === true;
  const open = cueVol != null || cueFlow ? true : stopcock === 'open' || flowing;

  const displayVol = cueVol != null ? cueVol : internalVol;
  const dispensed = Math.max(0, startRef.current - displayVol);

  // --- Dropwise delivery animation. A drop spawns at the tip and falls.
  const phaseRef = useRef({ t: 0, nextDrop: 0 });
  const dropsRef = useRef<Array<{ y: number; born: number }>>([]);
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((n) => (n + 1) % 1_000_000), []);

  const deliver = useCallback(
    (ml: number) => {
      setInternalVol((v) => {
        const next = Math.max(0, v - ml);
        onDispense?.({ volume: next, dispensed: Math.max(0, startRef.current - next) });
        return next;
      });
    },
    [onDispense],
  );

  // Single-drop on highlight 'drop' (rising-edge triggered in an effect).
  const lastDropCue = useRef(false);
  const wantDrop = cueOn && tokens.includes('drop');
  const cueVolNull = cueVol == null;
  useEffect(() => {
    if (wantDrop && !lastDropCue.current) {
      lastDropCue.current = true;
      dropsRef.current.push({ y: 0, born: phaseRef.current.t });
      if (cueVolNull) deliver(DROP_ML);
    } else if (!wantDrop) {
      lastDropCue.current = false;
    }
  }, [wantDrop, cueVolNull, deliver]);

  const animating = !reduced && (open || dropsRef.current.length > 0);
  useAnimationFrame((dt) => {
    const p = phaseRef.current;
    p.t += dt;
    // Stream drops while open & liquid remains.
    if (open && displayVol > 0) {
      p.nextDrop -= dt;
      if (p.nextDrop <= 0) {
        p.nextDrop = 0.28; // ~3.5 drops/sec
        dropsRef.current.push({ y: 0, born: p.t });
        // Only auto-decrement volume when the cue isn't already prescribing it.
        if (cueVol == null) deliver(DROP_ML);
      }
    }
    // Advance falling drops; remove off-screen.
    dropsRef.current = dropsRef.current
      .map((d) => ({ ...d, y: d.y + dt * 95 }))
      .filter((d) => d.y < 30);
    bump();
  }, animating);

  const fillPercentage = Math.max(0, Math.min(1, displayVol / maxVolume));
  const tubeTop = 15;
  const tubeBottom = 145;
  const tubeHeight = tubeBottom - tubeTop;
  const fillHeight = fillPercentage * tubeHeight;
  const fillY = tubeTop + 5;
  // Burette reads from the TOP: volume is what remains, so the surface is at the
  // top of the liquid column. Reading = (max - remaining) at the meniscus.
  const surfaceY = fillY + (tubeHeight - fillHeight);

  const containerStyle: CSSProperties = {
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease',
  };
  const stopcockStyle: CSSProperties = {
    cursor: disabled ? 'default' : onStopcock || true ? 'pointer' : 'default',
    transition: 'transform 0.3s ease',
  };

  const toggleStopcock = () => {
    if (disabled) return;
    onStopcock?.();
    setFlowing((f) => !f);
  };
  const addDrop = () => {
    if (disabled || displayVol <= 0) return;
    dropsRef.current.push({ y: 0, born: phaseRef.current.t });
    if (cueVol == null) deliver(DROP_ML);
    bump();
  };

  // Reading at the meniscus (mL delivered from 0 at top).
  const reading = maxVolume - displayVol;

  return (
    <div className={`flex flex-col items-center ${className}`} style={containerStyle}>
      <svg viewBox="0 0 86 210" width={108} height={266} role="img" aria-label={`Burette, ${displayVol.toFixed(2)} mL remaining`}>
        <defs>
          <linearGradient id={`glass-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(200,220,240,0.7)" />
            <stop offset="15%" stopColor="rgba(255,255,255,0.4)" />
            <stop offset="50%" stopColor="rgba(220,235,250,0.25)" />
            <stop offset="85%" stopColor="rgba(255,255,255,0.35)" />
            <stop offset="100%" stopColor="rgba(200,220,240,0.6)" />
          </linearGradient>
          <linearGradient id={`liquid-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={liquidColor} stopOpacity="0.8" />
            <stop offset="12%" stopColor={liquidColor} stopOpacity="0.9" />
            <stop offset="100%" stopColor={liquidColor} stopOpacity="0.96" />
          </linearGradient>
          <linearGradient id={`stopcock-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2C2C2C" />
            <stop offset="50%" stopColor="#5A5A5A" />
            <stop offset="100%" stopColor="#2C2C2C" />
          </linearGradient>
          <filter id={`shadow-${id}`} x="-20%" y="-5%" width="140%" height="110%">
            <feDropShadow dx="2" dy="2" stdDeviation="2" floodOpacity="0.25" />
          </filter>
          <clipPath id={`liquid-clip-${id}`}>
            <rect x="28" y={tubeTop} width="14" height={tubeHeight} />
          </clipPath>
        </defs>

        <g filter={`url(#shadow-${id})`}>
          <path d="M25 8 L25 15 L28 15 L28 8 L42 8 L42 15 L45 15 L45 8 Q45 3 35 3 Q25 3 25 8" fill={`url(#glass-${id})`} stroke="#7A9BB8" strokeWidth="1.5" />
          <ellipse cx="35" cy="5" rx="12" ry="3" fill="rgba(200,220,240,0.5)" stroke="#7A9BB8" strokeWidth="1" />
          <rect x="28" y="15" width="14" height="130" fill={`url(#glass-${id})`} stroke="#7A9BB8" strokeWidth="1.5" rx="1" />

          {displayVol > 0 && (
            <g clipPath={`url(#liquid-clip-${id})`}>
              <rect x="29" y={surfaceY} width="12" height={tubeBottom - surfaceY} fill={`url(#liquid-${id})`} />
              {/* Concave meniscus. */}
              <path d={`M29 ${surfaceY + 1.5} Q35 ${surfaceY - 1.5} 41 ${surfaceY + 1.5}`} fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="0.8" />
            </g>
          )}

          <line x1="30" y1="20" x2="30" y2="140" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinecap="round" />

          {/* Graduations: fine 0.5 mL ticks, numbered every 5 mL, 0 at TOP. */}
          {Array.from({ length: 21 }, (_, i) => {
            const markVolume = i * 2.5; // 0..50 in 2.5 mL steps
            const y = tubeTop + 5 + (markVolume / maxVolume) * (tubeHeight - 10);
            const isMajor = markVolume % 5 === 0;
            const isLabelled = markVolume % 5 === 0;
            return (
              <g key={i}>
                <line x1={42} y1={y} x2={isMajor ? 48 : 45} y2={y} stroke="#5A7A95" strokeWidth={isMajor ? 1.1 : 0.7} />
                {isLabelled && (
                  <text x="50" y={y + 2.4} fontSize="6" fill="#5A7A95" fontFamily="Arial, sans-serif">
                    {markVolume}
                  </text>
                )}
              </g>
            );
          })}

          <text x="50" y="16" fontSize="5.5" fill="#5A7A95" fontFamily="Arial, sans-serif">
            mL
          </text>

          {/* Reading pointer at the meniscus. */}
          {displayVol > 0 && (
            <g>
              <polygon points={`24,${surfaceY} 28,${surfaceY - 2.5} 28,${surfaceY + 2.5}`} fill="var(--ws-accent)" />
              <text x="22" y={surfaceY + 2.2} fontSize="5.5" fill="var(--ws-accent)" textAnchor="end" fontFamily="'Courier New', monospace" fontWeight="bold">
                {reading.toFixed(1)}
              </text>
            </g>
          )}

          {/* Stopcock — rotates the plug handle when open. */}
          <g style={stopcockStyle} onClick={toggleStopcock}>
            <rect x="28" y="146" width="14" height="14" fill={`url(#stopcock-${id})`} rx="2" />
            <circle cx="35" cy="153" r="4" fill="#1A1A1A" />
            <g transform={`rotate(${open ? 90 : 0} 35 153)`} style={{ transition: 'transform 0.3s ease' }}>
              <rect x="33" y="146" width="4" height="14" fill="var(--ws-accent)" rx="1.5" />
            </g>
          </g>

          {/* Delivery tip. */}
          <path d="M33 160 L33 175 Q33 180 35 180 Q37 180 37 175 L37 160" fill={`url(#glass-${id})`} stroke="#7A9BB8" strokeWidth="1" />
          <ellipse cx="35" cy="180" rx="1.5" ry="0.8" fill="#5A7A95" />

          {/* Falling drops. */}
          {dropsRef.current.map((d, i) => (
            <ellipse
              key={i}
              cx="35"
              cy={181 + d.y}
              rx="1.8"
              ry="2.6"
              fill={liquidColor}
              opacity={Math.max(0, 0.9 - d.y / 34)}
            />
          ))}
          {/* Pending drop at the tip while open. */}
          {open && displayVol > 0 && (
            <ellipse cx="35" cy="181" rx="1.6" ry="2.2" fill={liquidColor} opacity="0.85" />
          )}
        </g>
      </svg>

      {/* Readout + controls. */}
      <div className="mt-1 flex flex-col items-center gap-1">
        <div className="flex items-baseline gap-2 rounded-sm border border-ws-border bg-ws-surface-2 px-2 py-0.5">
          <span className="font-mono text-xs font-semibold text-ws-ink" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {reading.toFixed(2)}
          </span>
          <span className="text-[9px] text-ws-ink-3">mL delivered</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={disabled || displayVol <= 0}
            onClick={addDrop}
            className="rounded-sm border border-ws-border bg-ws-tile px-2 py-0.5 text-[10px] font-medium text-ws-ink-2 transition-colors hover:bg-ws-surface-2 active:brightness-95 disabled:opacity-40"
          >
            + Drop
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={toggleStopcock}
            aria-pressed={open}
            className={`rounded-sm px-2 py-0.5 text-[10px] font-semibold transition-colors active:brightness-95 disabled:opacity-40 ${
              open ? 'bg-ws-accent text-ws-on-accent' : 'border border-ws-border bg-ws-tile text-ws-ink-2 hover:bg-ws-surface-2'
            }`}
          >
            {open ? 'Flowing…' : 'Hold to flow'}
          </button>
        </div>
      </div>
    </div>
  );
}
